// Client wrapper for the `google-directions` edge function. Returns the
// route Google Maps would suggest for the same A→B + a small set of
// derived metrics (highway%, ETA delta) so the UI can show a "vs Google
// Maps" comparison without ever exposing the API key in the browser.

import { supabase } from "@/integrations/supabase/client";
import { setItemWithinBudget, GOOGLE_DIRECTIONS_BUDGET } from "@/utils/storageBudget";
import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";

export interface GoogleRoute {
  coordinates: LngLat[];
  distanceM: number;
  durationS: number;
  highwayM: number;
  summary: string;
  steps: {
    instruction: string;
    distanceM: number;
    isHighway: boolean;
    /** Google's structured maneuver string ("turn-left", "roundabout-right",
     *  "ramp-left", …) when the edge function passes it through. */
    maneuver?: string;
  }[];
}

// Two-tier cache:
//   1) In-memory map (deduplicates inflight + same-session requests).
//   2) localStorage (survives reloads / quick retries).
// The edge function ALSO caches in Postgres for ~7 days, so a fresh tab
// for a known trip pays only one Postgres round-trip — no Google call.
const cache = new Map<string, Promise<GoogleRoute | null>>();
const cacheKey = (a: LngLat, b: LngLat, mode: TravelMode) =>
  // Round to ~10 m so trivial GPS jitter doesn't bust the cache. Must
  // match the precision used by the edge function's DB cache key.
  `${mode}:${a[0].toFixed(4)},${a[1].toFixed(4)}->${b[0].toFixed(4)},${b[1].toFixed(4)}`;

const LS_PREFIX = "gdir:v1:";
// Mode-aware client TTL. Driving is now TRAFFIC_AWARE upstream, so its ETA is
// only good for a few minutes — match the server's 5-min driving window so a
// reload doesn't resurrect a stale traffic estimate. Walking/biking ignore
// traffic and stay cached for a day. The cache key is prefixed with the mode.
const LS_TTL_DRIVING_MS = 5 * 60 * 1000;
const LS_TTL_STATIC_MS = 24 * 60 * 60 * 1000;
const lsTtlForKey = (key: string) =>
  key.startsWith("driving:") ? LS_TTL_DRIVING_MS : LS_TTL_STATIC_MS;

function lsRead(key: string): GoogleRoute | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw) as { t: number; v: GoogleRoute };
    if (Date.now() - t > lsTtlForKey(key)) {
      localStorage.removeItem(LS_PREFIX + key);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

function lsWrite(key: string, value: GoogleRoute) {
  // Bounded via storageBudget.ts: one key per route, never swept before
  // Sep 16 2026. Makes room from our own keys before giving up.
  setItemWithinBudget(GOOGLE_DIRECTIONS_BUDGET, LS_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
}

/**
 * TRAFFIC TIMING (Morgan: "how do we improve traffic") — time ONE OF OUR
 * routes under Google's live traffic by pinning the request through
 * waypoints sampled along its polyline. Returns null when the deployed
 * edge function predates via support (it echoes viaCount when it honored
 * them) so callers can fall back instead of trusting a wrong-route time.
 */
// THE 502 STORM (Morgan, Aug 8, San Jose → Flagstaff console: three
// `google-directions` 502s per search). The Supabase edge logs show NO entry
// for those requests — every invocation that actually reached the function
// returned 200 — so the platform was rejecting them before boot: a burst of
// concurrent invocations of a heavy function (TRAFFIC_AWARE + 12 via points).
// The burst is ours: useTrafficEtas re-runs on every card change, and route
// generation re-classifies cards half a dozen times over ~28s, so the SAME
// route gets timed again and again.
//
// Two guards, no behaviour change when things are healthy:
//   1. In-flight + short-TTL memo per polyline — a re-render can't re-ask.
//   2. A single retry after a beat, because a 502 here is transient.
const timedEtaMemo = new Map<
  string,
  { at: number; value: Promise<{ durationS: number; distanceM: number } | null> }
>();
const TIMED_ETA_TTL_MS = 90_000;

function timedEtaKey(coordinates: LngLat[]): string {
  // Endpoints + length + a mid sample: enough to identify one polyline,
  // cheap enough to compute on every render.
  const a = coordinates[0];
  const b = coordinates[coordinates.length - 1];
  const m = coordinates[Math.floor(coordinates.length / 2)];
  return [a, m, b]
    .map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`)
    .join("|") + `|${coordinates.length}`;
}

export async function fetchGoogleTimedEta(
  coordinates: LngLat[],
): Promise<{ durationS: number; distanceM: number } | null> {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const memoKey = timedEtaKey(coordinates);
  const memo = timedEtaMemo.get(memoKey);
  if (memo && Date.now() - memo.at < TIMED_ETA_TTL_MS) return memo.value;
  const pending = fetchGoogleTimedEtaUncached(coordinates);
  timedEtaMemo.set(memoKey, { at: Date.now(), value: pending });
  return pending;
}

/** VIA_N evenly spaced pass-through points along a route.
 *
 *  ~12 of these pin the shape closely enough that Google times OUR road, not
 *  its own preference. Exported because a rating needs to record the same
 *  points it was timed through: origin + destination + this list IS the
 *  directions-cache key, so storing it lets a "felt off" replay the exact
 *  route months later instead of re-routing and hoping (Morgan, Aug 31). */
export const VIA_N = 12;

export function viaPointsFor(coordinates: LngLat[]): LngLat[] {
  const via: LngLat[] = [];
  for (let i = 1; i <= VIA_N; i++) {
    const idx = Math.round((coordinates.length - 1) * (i / (VIA_N + 1)));
    via.push(coordinates[idx]);
  }
  return via;
}

/** The via half of the directions-cache key, byte-identical to the string the
 *  google-directions function builds ("lng,lat;lng,lat;..." at 3 decimals).
 *  Null when the geometry is too short to sample. */
export function routeWaypointSignature(coordinates: LngLat[] | undefined): string | null {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  return viaPointsFor(coordinates)
    .map((c) => c[0].toFixed(3) + "," + c[1].toFixed(3))
    .join(";");
}

async function fetchGoogleTimedEtaUncached(
  coordinates: LngLat[],
): Promise<{ durationS: number; distanceM: number } | null> {
  const origin = coordinates[0];
  const destination = coordinates[coordinates.length - 1];
  const via = viaPointsFor(coordinates);
  const attempt = async (): Promise<{ durationS: number; distanceM: number } | null> => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase.functions.invoke("google-directions", {
      body: { origin, destination, mode: "driving", via },
    });
    if (error) throw error;          // let the retry see it
    if (!data) return null;
    const d = data as { durationS?: number; distanceM?: number; viaCount?: number };
    // Old deployment ignores via → its time is for GOOGLE's route, not
    // ours. Refuse it; the Mapbox fallback stays honest.
    if ((d.viaCount ?? 0) !== via.length) return null;
    if (!d.durationS || d.durationS <= 0) return null;
    return { durationS: d.durationS, distanceM: d.distanceM ?? 0 };
  };
  try {
    return await attempt();
  } catch {
    // ONE retry after a short beat. A 502 here means the platform turned the
    // invocation away before the function booted, which clears in a moment;
    // failing outright costs the card its live-traffic ETA for no reason.
    await new Promise((r) => setTimeout(r, 700));
    try {
      return await attempt();
    } catch {
      console.log("[google-eta] gave up after retry — Mapbox traffic time stands");
      return null;
    }
  }
}

export async function fetchGoogleRoute(
  origin: LngLat,
  destination: LngLat,
  mode: TravelMode,
): Promise<GoogleRoute | null> {
  const key = cacheKey(origin, destination, mode);
  const cached = cache.get(key);
  if (cached) return cached;

  // localStorage hit — instant, zero network. Re-seed the in-memory cache.
  const persisted = lsRead(key);
  if (persisted) {
    const p = Promise.resolve(persisted);
    cache.set(key, p);
    return p;
  }

  const promise = (async (): Promise<GoogleRoute | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("google-directions", {
        body: { origin, destination, mode },
      });
      if (error || !data) return null;
      const route = data as GoogleRoute;
      if (!Array.isArray(route.coordinates) || route.coordinates.length < 2) return null;
      lsWrite(key, route);
      return route;
    } catch {
      return null;
    }
  })();

  cache.set(key, promise);
  // Drop failed requests from the cache so the next trip retries.
  promise.then((r) => { if (!r) cache.delete(key); }).catch(() => cache.delete(key));
  return promise;
}

/** Highway-meters / total-meters as a percentage, 0..100. */
export function highwayPctOf(route: GoogleRoute): number {
  if (route.distanceM <= 0) return 0;
  return Math.max(0, Math.min(100, (route.highwayM / route.distanceM) * 100));
}
